import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import { api, clearSession } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import LangToggle from '@/components/LangToggle';
import { pwPolicyError } from '@/lib/passwordPolicy';

function PwField({ testid, label, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="as-label">{label}</label>
      <div className="mt-2 flex items-center rounded-xl border border-white/[0.06] bg-black/30 transition-colors focus-within:border-white/20">
        <input
          data-testid={testid}
          className="h-14 flex-1 bg-transparent px-4 text-[15px] text-white placeholder:text-asgym-muted outline-none"
          type={show ? 'text' : 'password'}
          placeholder="••••••••"
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button type="button" data-testid={`${testid}-toggle`} onClick={() => setShow((s) => !s)} className="px-4 text-asgym-sub">
          {show ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [nw, setNw] = useState('');
  const [cf, setCf] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    const errKey = pwPolicyError(nw, { confirmPw: cf });
    if (errKey) return setError(t(errKey));
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: nw });
      setNw(''); setCf('');
      // No auto-login: drop any stale local session so the user is truly returned
      // to the login page (the old JWT is already invalid server-side).
      clearSession();
      sessionStorage.setItem('pw_changed', '1');
      window.location.href = '/';
    } catch (err) {
      // Generic, localized message (invalid / expired / used token).
      setError(t('reset.invalidToken'));
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="relative min-h-screen w-full overflow-hidden bg-asgym-bgdeep">
        <LangToggle className="absolute right-5 top-5 z-20" />
        <div className="relative mx-auto flex min-h-screen w-full max-w-[440px] flex-col justify-center px-6 py-10">
          <div className="rounded-3xl border border-white/[0.06] bg-[#16171A]/80 p-6 text-center" data-testid="reset-missing-token">
            <h2 className="font-display text-2xl font-extrabold text-white">{t('reset.invalidToken')}</h2>
            <button type="button" data-testid="reset-request-new" onClick={() => nav('/forgot-password')} className="as-btn-primary mt-6 w-full">
              {t('reset.requestNew')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-asgym-bgdeep">
      <LangToggle className="absolute right-5 top-5 z-20" />
      <div className="relative mx-auto flex min-h-screen w-full max-w-[440px] flex-col justify-center px-6 py-10">
        <form
          onSubmit={submit}
          className="rounded-3xl border border-white/[0.06] bg-[#16171A]/80 p-6 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl animate-fade-up"
          data-testid="reset-card"
        >
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-asgym-accent/15">
            <ShieldCheck className="text-asgym-accent" size={26} />
          </div>
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-white">{t('reset.title')}</h2>
          <p className="mb-6 mt-1.5 text-[13px] text-asgym-sub">{t('reset.sub')}</p>

          <div className="space-y-3">
            <PwField testid="reset-new" label={t('pw.new')} value={nw} onChange={setNw} />
            <PwField testid="reset-confirm" label={t('pw.confirm')} value={cf} onChange={setCf} />
          </div>
          <p className="mt-2 text-[12px] text-asgym-muted">{t('pw.hint')}</p>
          {error && <p data-testid="reset-error" className="mt-2 text-[13px] font-semibold text-asgym-error">{error}</p>}

          <button type="submit" data-testid="reset-submit" disabled={loading} className="as-btn-primary mt-6 w-full">
            {loading ? <Loader2 className="animate-spin" size={20} /> : t('reset.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
